import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Drawer, FormControlLabel, IconButton, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { useSnackbar } from "notistack";
import moment from "moment";
import {
  clearFeedbackDraftApi,
  getReviewProgressApi,
  removeFeedbackMediaApi,
  saveFeedbackDraftApi,
  uploadFeedbackMediaApi,
} from "src/DAL/coachWorkspace/coachWorkspace";
import { sendSubmissionFeedbackApi } from "src/pages/Trackers/submissionStorage";
import { CW, InfoHint, Panel, Pill, TextLink } from "./cwUi";
import LightEditor from "./LightEditor";
import MediaRecorderPanel, { canRecord } from "./MediaRecorderPanel";

/**
 * P4.3 / P4.4 / P4.7 — feedback composer. layout="page" = the feedback
 * screen of design PDF page 7 (Your feedback + Recordings on the left;
 * Coach notebook, Send to, Progress included on the right; Save draft /
 * Preview feedback). layout="panel" = the earlier inline panel. Replaces, on the NEW review page, the old feedback
 * drawer (ClientFeedbackDrawer). The old drawer still works on the old
 * Submission page.
 *
 *  - Type, or Dictate (browser speech-to-text, Chrome / Edge / Safari)
 *  - Draft auto-saves (submission.feedbackDraft) — come back later
 *  - "Insert selected": select text anywhere on the page (answers, notes)
 *  - Progress from the form answers (4.7), on/off + refresh
 *  - Voice note / video: record OR choose a file → saved on S3
 *  - Preview → Send (chat and / or email) with the existing feedback API;
 *    recordings go as links in the message (in-chat player = point 4.6).
 */

const SAVE_AFTER_MS = 1500;

/**
 * Recording in the browser (Record voice note / Record video) is switched OFF
 * for now (3 Nov 2026, client request): coaches choose an audio or video FILE
 * instead (still saved on S3). Set to true to bring the record buttons back —
 * MediaRecorderPanel is unchanged.
 */
const RECORDING_ON = false;

const isEmptyHtml = (html) =>
  !String(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .trim();

const escapeHtml = (v) =>
  String(v == null ? "" : v).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

const clock = (s) => (s == null ? "" : `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")}`);

/** Message as the member gets it: text + progress + recording links. */
export const composeFeedbackHtml = ({ message, includeProgress, progressHtml, media = [] }) => {
  const parts = [];
  if (!isEmptyHtml(message)) parts.push(message);
  if (includeProgress && progressHtml) parts.push(progressHtml);
  media.forEach((m) => {
    const what = m.kind === "video" ? "Video from your coach" : "Voice note from your coach";
    const action = m.kind === "video" ? "Watch the video" : "Listen to the voice note";
    parts.push(
      `<p><strong>${what}</strong>${m.durationSec ? ` (${clock(m.durationSec)})` : ""}: <a href="${escapeHtml(m.url)}" target="_blank" rel="noopener noreferrer">${action}</a></p>`
    );
  });
  return parts.join("");
};

const getRecognition = () => {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

function MediaItem({ item, onRemove, busy }) {
  return (
    <Box sx={{ border: `1px solid ${CW.line}`, borderRadius: "10px", p: 1, bgcolor: "#fff" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
        <Pill tone="teal">{item.kind === "video" ? "Video" : "Voice note"}</Pill>
        <Typography sx={{ fontSize: 12.5, color: CW.muted, flex: 1, minWidth: 0 }} noWrap>
          {item.source === "recorded" ? "Recorded" : item.name}
          {item.durationSec ? ` · ${clock(item.durationSec)}` : ""}
          {item.size ? ` · ${(item.size / (1024 * 1024)).toFixed(1)} MB` : ""}
        </Typography>
        <IconButton size="small" aria-label="Remove recording" disabled={busy} onClick={() => onRemove(item)}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
      {item.kind === "video" ? (
        <video src={item.url} controls playsInline preload="metadata" style={{ width: "100%", maxHeight: 240, borderRadius: 8, background: "#000" }} />
      ) : (
        <audio src={item.url} controls preload="metadata" style={{ width: "100%" }} />
      )}
    </Box>
  );
}

export default function FeedbackComposer({ submission, initialDraft, onClose, onSent, layout = "panel", renderNotebook, onOpenPlan, clientName }) {
  const { enqueueSnackbar } = useSnackbar();
  const submissionId = submission.id;
  const editorRef = useRef(null);
  const timerRef = useRef(null);
  const recognitionRef = useRef(null);
  const selectionRef = useRef("");
  const fileRef = useRef(null);
  const audioFileRef = useRef(null);
  const videoFileRef = useRef(null);
  const rootRef = useRef(null);

  const [message, setMessage] = useState(initialDraft?.message || "");
  const [includeProgress, setIncludeProgress] = useState(initialDraft?.includeProgress !== false);
  // MOBILE-SHELL (3 Nov, PDF page 3 "Notes while drafting … or in a mobile
  // drawer"): below "lg" the notebook opens in a bottom drawer.
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down("lg"));
  const [notesOpen, setNotesOpen] = useState(false);
  const [media, setMedia] = useState(initialDraft?.media || []);
  const [savedAt, setSavedAt] = useState(initialDraft?.updatedAt || null);
  const [saveState, setSaveState] = useState("idle");
  const [progress, setProgress] = useState(null);
  const [progressLoading, setProgressLoading] = useState(false);
  const [recorder, setRecorder] = useState(null); // "audio" | "video" | null
  const [uploading, setUploading] = useState(null); // { name, percent }
  const [dictating, setDictating] = useState(false);
  const [interim, setInterim] = useState("");
  const [hasSelection, setHasSelection] = useState(false);
  const [sendChat, setSendChat] = useState(Boolean(submission.memberId));
  const [sendEmail, setSendEmail] = useState(Boolean(submission.email));
  const [preview, setPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState(null);

  // ── Progress (4.7) ───────────────────────────────────────────────────
  const loadProgress = useCallback(async () => {
    setProgressLoading(true);
    const result = await getReviewProgressApi(submissionId);
    setProgress(result?.code === 200 ? result : { lines: [], html: "" });
    setProgressLoading(false);
  }, [submissionId]);

  useEffect(() => {
    loadProgress();
  }, [loadProgress]);

  // ── Draft auto-save (4.3) ────────────────────────────────────────────
  const saveDraft = useCallback(
    async (patch) => {
      setSaveState("saving");
      const result = await saveFeedbackDraftApi(submissionId, patch);
      if (result?.code === 200) {
        setSavedAt(result.draft?.updatedAt || new Date());
        setSaveState("saved");
      } else setSaveState("error");
    },
    [submissionId]
  );

  const onEditorChange = (html) => {
    setMessage(html);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => saveDraft({ message: html }), SAVE_AFTER_MS);
  };

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (_err) {
          /* ignore */
        }
      }
    },
    []
  );

  // ── "Insert selected" — remember text selected outside the composer ──
  useEffect(() => {
    const onSelection = () => {
      const sel = window.getSelection && window.getSelection();
      const text = sel ? String(sel.toString() || "").trim() : "";
      const inside = sel && sel.anchorNode && rootRef.current && rootRef.current.contains(sel.anchorNode);
      if (text && !inside) {
        selectionRef.current = text.slice(0, 2000);
        setHasSelection(true);
      }
    };
    document.addEventListener("selectionchange", onSelection);
    return () => document.removeEventListener("selectionchange", onSelection);
  }, []);

  const insertSelected = () => {
    const text = selectionRef.current;
    if (!text || !editorRef.current) {
      enqueueSnackbar("Select some text on the page first (answers, notes…)", { variant: "info" });
      return;
    }
    const html = `<blockquote>${escapeHtml(text).replace(/\n/g, "<br>")}</blockquote><p><br></p>`;
    editorRef.current.insertHtml(html);
    selectionRef.current = "";
    setHasSelection(false);
  };

  // ── Dictate (browser speech to text) ─────────────────────────────────
  const Recognition = getRecognition();
  const toggleDictate = () => {
    if (dictating) {
      try {
        recognitionRef.current && recognitionRef.current.stop();
      } catch (_err) {
        /* ignore */
      }
      setDictating(false);
      setInterim("");
      return;
    }
    if (!Recognition) {
      enqueueSnackbar("Dictation works in Chrome, Edge or Safari", { variant: "warning" });
      return;
    }
    const rec = new Recognition();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = (navigator.language || "en-GB").startsWith("en") ? navigator.language : "en-GB";
    rec.onresult = (event) => {
      let live = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const r = event.results[i];
        if (r.isFinal) {
          const text = String(r[0].transcript || "").trim();
          if (text && editorRef.current) editorRef.current.insertText(`${text} `);
        } else live += r[0].transcript;
      }
      setInterim(live);
    };
    rec.onerror = (e) => {
      if (e && e.error === "not-allowed") enqueueSnackbar("Allow microphone access to dictate", { variant: "warning" });
      setDictating(false);
      setInterim("");
    };
    rec.onend = () => {
      setDictating(false);
      setInterim("");
    };
    recognitionRef.current = rec;
    try {
      rec.start();
      setDictating(true);
    } catch (_err) {
      setDictating(false);
    }
  };

  // ── Media (4.4 / S3) ─────────────────────────────────────────────────
  const upload = async (file, { source = "uploaded", durationSec, onProgress } = {}) => {
    setUploading({ name: file.name, percent: 0 });
    const result = await uploadFeedbackMediaApi(submissionId, file, {
      source,
      durationSec,
      onProgress: (p) => {
        setUploading({ name: file.name, percent: p });
        if (onProgress) onProgress(p);
      },
    });
    setUploading(null);
    if (result?.code === 200) {
      setMedia(result.draft?.media || []);
      setSavedAt(result.draft?.updatedAt || new Date());
      enqueueSnackbar(result.message || "Added", { variant: "success" });
      return true;
    }
    enqueueSnackbar(result?.message || "Upload failed", { variant: "error" });
    return false;
  };

  const onChooseFile = async (e, kind) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    const want = kind === "audio" ? /^audio\// : kind === "video" ? /^video\// : /^(audio|video)\//;
    if (!want.test(file.type)) {
      enqueueSnackbar(
        kind === "audio" ? "Choose an audio file (mp3, m4a, wav…)" : kind === "video" ? "Choose a video file (mp4, mov, webm…)" : "Choose an audio or video file",
        { variant: "warning" }
      );
      return;
    }
    if (file.size > 150 * 1024 * 1024) {
      enqueueSnackbar("File is too big (max 150 MB)", { variant: "warning" });
      return;
    }
    await upload(file, { source: "uploaded" });
  };

  const removeMedia = async (item) => {
    setBusy(true);
    const result = await removeFeedbackMediaApi(submissionId, item.id);
    setBusy(false);
    if (result?.code === 200) setMedia(result.draft?.media || []);
    else enqueueSnackbar(result?.message || "Could not remove", { variant: "error" });
  };

  // ── Send ─────────────────────────────────────────────────────────────
  const finalHtml = useMemo(
    () => composeFeedbackHtml({ message, includeProgress, progressHtml: progress?.html || "", media }),
    [message, includeProgress, progress, media]
  );
  const canSend = !isEmptyHtml(message) || media.length > 0;

  // PREVIEW-HINT (3 Nov): the button never looks active-but-dead. With no
  // text and no file, a click says what is missing and puts the cursor in the
  // feedback box; otherwise the preview opens.
  const openPreview = () => {
    if (!canSend) {
      enqueueSnackbar("Write your feedback (or add a voice / video file) before previewing", { variant: "info" });
      if (editorRef.current && editorRef.current.focus) editorRef.current.focus();
      return;
    }
    setPreview(true);
  };

  const send = async () => {
    if (!canSend) return;
    if (!sendChat && !sendEmail) {
      enqueueSnackbar("Pick chat and / or email", { variant: "warning" });
      return;
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    setSending(true);
    const result = await sendSubmissionFeedbackApi(submissionId, { message: finalHtml, sendChat, sendEmail });
    setSending(false);
    if (!result?.ok) {
      enqueueSnackbar(result?.message || "Couldn't send the feedback", { variant: "error" });
      return;
    }
    const failed = Object.entries(result.results || {})
      .filter(([, r]) => r && r.sent === false && r.error)
      .map(([key, r]) => `${key}: ${r.error}`);
    if (failed.length) enqueueSnackbar(`Sent, but ${failed.join("; ")}`, { variant: "warning" });
    else enqueueSnackbar("Feedback sent", { variant: "success" });
    await clearFeedbackDraftApi(submissionId);
    if (onSent) onSent(result);
  };

  const draftLabel =
    saveState === "saving"
      ? "Saving draft…"
      : saveState === "error"
        ? "Draft not saved — check connection"
        : savedAt
          ? `Draft saved ${moment.min(moment(savedAt), moment()).fromNow()}`
          : "Draft saves as you type";

  const tool = { fontSize: 12, padding: "3px 10px" };

  const saveNow = async () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const html = editorRef.current ? editorRef.current.getHtml() : message;
    await saveDraft({ message: html, includeProgress });
    enqueueSnackbar("Draft saved", { variant: "success" });
  };

  if (layout === "page") {
    const first = submission.firstName || String(clientName || "").split(" ")[0] || "the client";
    const modeBtn = (on) => ({
      padding: "10px 16px",
      minWidth: 110,
      flex: "1 1 auto",
      ...(on ? {} : { color: CW.ink, borderColor: CW.line }),
    });
    const notebookFooter = (
                  <Box sx={{ mt: 2 }}>
                    <button
                      type="button"
                      className="small-contained-outlined-button"
                      style={{ width: "100%", padding: "10px 14px", color: CW.ink, borderColor: CW.line }}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={insertSelected}
                      disabled={!hasSelection}
                      title="Select text in the notebook (or anywhere on the page), then press this"
                    >
                      Insert selected into draft
                    </button>
                    <Typography sx={{ fontSize: 12, color: CW.muted, mt: 0.75 }}>
                      Only selected text enters the draft. Edit before sending.
                    </Typography>
                  </Box>
    );
    const splitLine = (text) => {
      const at = String(text).indexOf(":");
      return at > 0 ? [text.slice(0, at), text.slice(at + 1).trim()] : [text, ""];
    };
    return (
      <Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) 420px" },
            gap: 2.5,
            alignItems: "start",
          }}
        >
          <Panel
            title="Your feedback"
            info="feedback.compose"
            action={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                {phone && renderNotebook ? (
                  <TextLink onClick={() => setNotesOpen(true)} sx={{ fontSize: 13.5 }} data-testid="open-notes-drawer">
                    Notes
                  </TextLink>
                ) : null}
                <Typography sx={{ fontSize: 12.5, color: saveState === "error" ? CW.red : CW.muted }}>{draftLabel}</Typography>
              </Box>
            }
          >
            <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap", mb: 1.5 }}>
              <button
                type="button"
                className={!dictating && !recorder ? "small-contained-button" : "small-contained-outlined-button"}
                style={modeBtn(!dictating && !recorder)}
                onClick={() => {
                  if (dictating) toggleDictate();
                  setRecorder(null);
                  if (editorRef.current && editorRef.current.focus) editorRef.current.focus();
                }}
              >
                Type
              </button>
              <button
                type="button"
                className={dictating ? "small-contained-button" : "small-contained-outlined-button"}
                style={modeBtn(dictating)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={toggleDictate}
                title={Recognition ? "Speak — your words are typed" : "Dictation works in Chrome, Edge or Safari"}
              >
                {dictating ? "■ Stop dictating" : "Dictate"}
              </button>
              {RECORDING_ON ? (
                <>
                  <button
                    type="button"
                    className={recorder === "audio" ? "small-contained-button" : "small-contained-outlined-button"}
                    style={modeBtn(recorder === "audio")}
                    disabled={!canRecord() || Boolean(uploading)}
                    onClick={() => setRecorder("audio")}
                  >
                    Record voice note
                  </button>
                  <button
                    type="button"
                    className={recorder === "video" ? "small-contained-button" : "small-contained-outlined-button"}
                    style={modeBtn(recorder === "video")}
                    disabled={!canRecord() || Boolean(uploading)}
                    onClick={() => setRecorder("video")}
                  >
                    Record video
                  </button>
                </>
              ) : null}
              <button
                type="button"
                className="small-contained-outlined-button"
                style={modeBtn(false)}
                disabled={Boolean(uploading)}
                onClick={() => audioFileRef.current && audioFileRef.current.click()}
                title="Choose an audio file from your computer or phone (max 150 MB)"
              >
                Voice note file
              </button>
              <button
                type="button"
                className="small-contained-outlined-button"
                style={modeBtn(false)}
                disabled={Boolean(uploading)}
                onClick={() => videoFileRef.current && videoFileRef.current.click()}
                title="Choose a video file from your computer or phone (max 150 MB)"
              >
                Video file
              </button>
              <input ref={audioFileRef} type="file" accept="audio/*" hidden onChange={(e) => onChooseFile(e, "audio")} data-testid="audio-file-input" />
              <input ref={videoFileRef} type="file" accept="video/*" hidden onChange={(e) => onChooseFile(e, "video")} data-testid="video-file-input" />
            </Box>
            <Typography sx={{ fontSize: 13, color: CW.muted, mb: 1.5 }}>
              {includeProgress
                ? "Progress from this check-in is added when you send (see Progress included). Edit before sending."
                : "Write your feedback. Progress is not included."}
            </Typography>
            {recorder ? (
              <Box sx={{ mb: 1.5 }}>
                <MediaRecorderPanel kind={recorder} onUpload={upload} onClose={() => setRecorder(null)} />
              </Box>
            ) : null}
            <Box ref={rootRef} data-testid="feedback-composer" sx={{ bgcolor: CW.soft, border: `1px solid ${CW.line}`, borderRadius: "12px", p: 1.25 }}>
              <LightEditor
                ref={editorRef}
                initialHtml={initialDraft?.message || ""}
                onChange={onEditorChange}
                minHeight={260}
                maxHeight={560}
                testId="feedback-editor"
                placeholder={`Hi ${first}, …  Add your coaching feedback and next steps here.`}
              />
              <Typography sx={{ fontSize: 12, color: CW.muted, mt: 1, px: 0.5 }}>
                Editable text · Insert selected notes, then adapt the wording for {first}
              </Typography>
            </Box>
            {dictating ? (
              <Typography sx={{ fontSize: 12.5, color: CW.primary, mt: 0.75 }}>
                Listening… {interim ? <i style={{ color: CW.muted }}>{interim}</i> : null}
              </Typography>
            ) : null}

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 3, mb: 1.25 }}>
              <Typography sx={{ fontSize: 19, fontWeight: 700, color: CW.ink }}>
                Recordings
                <InfoHint id="feedback.recordings" />
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: CW.muted }}>Playback before sending</Typography>
            </Box>
            {RECORDING_ON && !canRecord() ? (
              <Typography sx={{ fontSize: 12, color: CW.muted, mb: 1 }}>This browser can't record — use Choose file.</Typography>
            ) : null}
            {uploading ? (
              <Typography sx={{ fontSize: 12.5, color: CW.muted, mb: 1 }}>
                Uploading {uploading.name}… {uploading.percent ? `${uploading.percent}%` : ""}
              </Typography>
            ) : null}
            {media.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: CW.muted, border: `1px dashed ${CW.line}`, borderRadius: "12px", p: 2 }}>
                No voice note or video yet. Use "Voice note file" or "Video file" above (max 150 MB each).
              </Typography>
            ) : (
              <Box sx={{ display: "grid", gap: 1.25 }}>
                {media.map((item) => (
                  <Box key={item.id} sx={{ border: `1px solid ${CW.line}`, borderRadius: "12px", p: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <IconButton
                        aria-label={playing === item.id ? "Hide player" : "Play"}
                        onClick={() => setPlaying(playing === item.id ? null : item.id)}
                        sx={{ bgcolor: CW.tealSoft, color: CW.primary, width: 40, height: 40, fontSize: 14 }}
                      >
                        {playing === item.id ? "■" : "▶"}
                      </IconButton>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 15, fontWeight: 700, color: CW.ink }} noWrap>
                          {item.kind === "video" ? "Video feedback" : "Voice note"}
                          {item.durationSec ? ` · ${clock(item.durationSec)}` : ""}
                        </Typography>
                        <Typography sx={{ fontSize: 12.5, color: CW.muted }} noWrap>
                          {item.source === "recorded" ? "Recorded" : item.name} · Uploaded · Ready to include
                        </Typography>
                      </Box>
                      <TextLink onClick={() => removeMedia(item)} disabled={busy} sx={{ fontSize: 13.5 }}>
                        Remove
                      </TextLink>
                    </Box>
                    {playing === item.id ? (
                      <Box sx={{ mt: 1.25 }}>
                        {item.kind === "video" ? (
                          <video src={item.url} controls autoPlay playsInline style={{ width: "100%", maxHeight: 300, borderRadius: 8, background: "#000" }} />
                        ) : (
                          <audio src={item.url} controls autoPlay style={{ width: "100%" }} />
                        )}
                      </Box>
                    ) : null}
                  </Box>
                ))}
              </Box>
            )}
            <Typography sx={{ fontSize: 13, color: CW.muted, mt: 3 }}>
              Checklist saved separately · Sending leaves its answers intact.
            </Typography>
            {onOpenPlan ? (
              <TextLink onClick={onOpenPlan} sx={{ mt: 1.5, display: "inline-block" }}>
                Review plan and skips →
              </TextLink>
            ) : null}
          </Panel>

          <Box sx={{ display: "grid", gap: 2.5, minWidth: 0 }}>
            {renderNotebook && !phone ? renderNotebook(notebookFooter) : null}

            <Panel title={`Send to ${first}`} info="feedback.sendto">
              <FormControlLabel
                sx={{ m: 0, alignItems: "flex-start", display: "flex" }}
                control={<Checkbox checked={sendChat} disabled={!submission.memberId} onChange={(e) => setSendChat(e.target.checked)} />}
                label={
                  <Box sx={{ pt: 0.75 }}>
                    <Typography sx={{ fontSize: 15.5, fontWeight: 700, color: CW.ink }}>Chat message</Typography>
                    <Typography sx={{ fontSize: 13, color: CW.muted }}>
                      {submission.memberId ? `${first}'s GymRite conversation` : "No portal account"}
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                sx={{ m: 0, mt: 1.5, alignItems: "flex-start", display: "flex" }}
                control={<Checkbox checked={sendEmail} disabled={!submission.email} onChange={(e) => setSendEmail(e.target.checked)} />}
                label={
                  <Box sx={{ pt: 0.75, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 15.5, fontWeight: 700, color: CW.ink }}>Email</Typography>
                    <Typography sx={{ fontSize: 13, color: CW.muted, wordBreak: "break-all" }}>{submission.email || "No email on file"}</Typography>
                  </Box>
                }
              />
              <Typography sx={{ fontSize: 12.5, color: CW.muted, borderTop: `1px solid ${CW.line}`, pt: 1.5, mt: 2 }}>
                Recordings go as a link in the chat message and in the email.
              </Typography>
            </Panel>

            <Panel
              title="Progress included"
              info="feedback.progress"
              action={
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={includeProgress}
                      onChange={(e) => {
                        setIncludeProgress(e.target.checked);
                        saveDraft({ includeProgress: e.target.checked });
                      }}
                    />
                  }
                  label={<Typography sx={{ fontSize: 12.5, color: CW.muted }}>Include</Typography>}
                />
              }
            >
              {progress && progress.lines && progress.lines.length ? (
                <Box data-testid="progress-lines" sx={{ opacity: includeProgress ? 1 : 0.5 }}>
                  {progress.lines.map((line) => {
                    const [label, rest] = splitLine(line.text);
                    return (
                      <Box key={line.label} sx={{ display: "flex", justifyContent: "space-between", gap: 2, py: 0.5 }}>
                        <Typography sx={{ fontSize: 13.5, color: CW.muted }}>{label}</Typography>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: CW.ink, textAlign: "right" }}>{rest}</Typography>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Typography sx={{ fontSize: 12.5, color: CW.muted }}>
                  {progressLoading ? "Loading…" : "No measurable answers yet (weight, scores…)."}
                </Typography>
              )}
              <TextLink onClick={loadProgress} disabled={progressLoading} sx={{ mt: 1.5, fontSize: 13.5 }}>
                {progressLoading ? "Refreshing…" : "Refresh progress…"}
              </TextLink>
            </Panel>
          </Box>
        </Box>

        {!submission.memberId && !submission.email ? (
          <Alert severity="warning" sx={{ mt: 2 }}>
            This person has no portal account or email — feedback can't be sent.
          </Alert>
        ) : null}

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center", justifyContent: "space-between", mt: 2.5 }}>
          <Typography sx={{ fontSize: 13.5, color: CW.muted }}>
            Preview the selected channels before sending.
            <InfoHint id="feedback.bottom" />
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <button type="button" className="small-contained-outlined-button" style={{ padding: "11px 22px", minWidth: 170, color: CW.ink, borderColor: CW.line }} onClick={saveNow}>
              Save draft
            </button>
            <button
              type="button"
              className="small-contained-button"
              style={{ padding: "11px 22px", minWidth: 240, opacity: canSend ? 1 : 0.55 }}
              onClick={openPreview}
              title={canSend ? undefined : "Write your feedback first"}
              data-testid="preview-feedback"
            >
              Preview feedback
            </button>
          </Box>
        </Box>

        {phone && renderNotebook ? (
          <Drawer
            anchor="bottom"
            open={notesOpen}
            onClose={() => setNotesOpen(false)}
            PaperProps={{ sx: { maxHeight: "85vh", borderRadius: "16px 16px 0 0", bgcolor: CW.soft } }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2, pt: 1.5 }}>
              <Typography sx={{ fontSize: 13, color: CW.muted }}>Notes while drafting</Typography>
              <IconButton size="small" aria-label="Close notes" onClick={() => setNotesOpen(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ p: 2, pt: 1 }} data-testid="notes-drawer">
              {renderNotebook(notebookFooter)}
            </Box>
          </Drawer>
        ) : null}
        <Dialog open={preview} onClose={() => setPreview(false)} fullWidth maxWidth="sm">
          <DialogTitle sx={{ fontWeight: 700 }}>Preview — what {first} gets</DialogTitle>
          <DialogContent dividers>
            <Typography sx={{ fontSize: 12.5, color: CW.muted, mb: 1 }}>
              {[sendChat ? "Chat message" : null, sendEmail ? `Email to ${submission.email}` : null].filter(Boolean).join(" · ") ||
                "Pick chat and / or email"}
            </Typography>
            <Box
              data-testid="feedback-preview"
              sx={{ border: `1px dashed ${CW.line}`, borderRadius: "10px", p: 1.5, fontSize: 14, "& p": { m: "0 0 6px" }, wordBreak: "break-word" }}
              dangerouslySetInnerHTML={{ __html: finalHtml || "<p><i>Nothing yet</i></p>" }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 1.5 }}>
            <button type="button" className="small-contained-outlined-button" onClick={() => setPreview(false)}>
              Back to editing
            </button>
            <button
              type="button"
              className="small-contained-button"
              onClick={send}
              disabled={!canSend || sending || Boolean(uploading) || (!sendChat && !sendEmail)}
            >
              {sending ? "Sending…" : "Send feedback"}
            </button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  return (
    <Panel
      title="Feedback"
      action={
        <IconButton size="small" aria-label="Close feedback" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      }
      sx={{ borderColor: CW.primary }}
    >
      <Box ref={rootRef} data-testid="feedback-composer">
        <LightEditor
          ref={editorRef}
          initialHtml={initialDraft?.message || ""}
          onChange={onEditorChange}
          minHeight={160}
          testId="feedback-editor"
          placeholder="Write your feedback, or press Dictate and speak…"
          toolbarExtra={
            <>
              <button
                type="button"
                className={dictating ? "small-contained-button" : "small-contained-outlined-button"}
                style={tool}
                onMouseDown={(e) => e.preventDefault()}
                onClick={toggleDictate}
                title={Recognition ? "Speak — your words are typed" : "Dictation works in Chrome, Edge or Safari"}
              >
                {dictating ? "■ Stop dictating" : "🎙 Dictate"}
              </button>
              <button
                type="button"
                className="small-contained-outlined-button"
                style={tool}
                onMouseDown={(e) => e.preventDefault()}
                onClick={insertSelected}
                disabled={!hasSelection}
                title="Select text in the answers or notes, then press this"
              >
                Insert selected
              </button>
            </>
          }
        />
        {dictating ? (
          <Typography sx={{ fontSize: 12.5, color: CW.primary, mt: 0.5 }}>
            Listening… {interim ? <i style={{ color: CW.muted }}>{interim}</i> : null}
          </Typography>
        ) : null}
        <Typography sx={{ fontSize: 12, color: saveState === "error" ? CW.red : CW.muted, mt: 0.5 }}>{draftLabel}</Typography>

        {/* 4.7 Progress */}
        <Box sx={{ mt: 2, p: 1.25, borderRadius: "10px", bgcolor: CW.soft }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <FormControlLabel
              sx={{ m: 0, flex: 1 }}
              control={
                <Checkbox
                  size="small"
                  checked={includeProgress}
                  onChange={(e) => {
                    setIncludeProgress(e.target.checked);
                    saveDraft({ includeProgress: e.target.checked });
                  }}
                />
              }
              label={<Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>Include progress</Typography>}
            />
            <button type="button" className="small-contained-outlined-button" style={tool} onClick={loadProgress} disabled={progressLoading}>
              {progressLoading ? "Refreshing…" : "Refresh"}
            </button>
          </Box>
          {progress && progress.lines && progress.lines.length ? (
            <Box component="ul" sx={{ m: 0, pl: 3, opacity: includeProgress ? 1 : 0.5 }} data-testid="progress-lines">
              {progress.lines.map((line) => (
                <li key={line.label}>
                  <Typography sx={{ fontSize: 13, color: CW.ink }}>{line.text}</Typography>
                </li>
              ))}
            </Box>
          ) : (
            <Typography sx={{ fontSize: 12.5, color: CW.muted }}>
              {progressLoading ? "Loading…" : "No measurable answers yet (weight, scores…)."}
            </Typography>
          )}
        </Box>

        {/* 4.4 Voice / video */}
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: CW.ink, mr: 1 }}>Voice / video</Typography>
            {RECORDING_ON ? (
              <>
                <button type="button" className="small-contained-outlined-button" style={tool} disabled={!canRecord() || Boolean(recorder) || Boolean(uploading)} onClick={() => setRecorder("audio")}>
                  Record voice
                </button>
                <button type="button" className="small-contained-outlined-button" style={tool} disabled={!canRecord() || Boolean(recorder) || Boolean(uploading)} onClick={() => setRecorder("video")}>
                  Record video
                </button>
              </>
            ) : null}
            <button type="button" className="small-contained-outlined-button" style={tool} disabled={Boolean(uploading)} onClick={() => fileRef.current && fileRef.current.click()}>
              Choose file
            </button>
            <input ref={fileRef} type="file" accept="audio/*,video/*" hidden onChange={onChooseFile} data-testid="media-file-input" />
          </Box>
          {RECORDING_ON && !canRecord() ? (
            <Typography sx={{ fontSize: 12, color: CW.muted, mt: 0.5 }}>This browser can't record — use Choose file.</Typography>
          ) : null}
          {recorder ? (
            <Box sx={{ mt: 1 }}>
              <MediaRecorderPanel kind={recorder} onUpload={upload} onClose={() => setRecorder(null)} />
            </Box>
          ) : null}
          {uploading && !recorder ? (
            <Typography sx={{ fontSize: 12.5, color: CW.muted, mt: 1 }}>
              Uploading {uploading.name}… {uploading.percent ? `${uploading.percent}%` : ""}
            </Typography>
          ) : null}
          {media.length ? (
            <Box sx={{ display: "grid", gap: 1, mt: 1.25, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" } }}>
              {media.map((item) => (
                <MediaItem key={item.id} item={item} onRemove={removeMedia} busy={busy} />
              ))}
            </Box>
          ) : null}
        </Box>

        {/* Preview + send */}
        {preview ? (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: CW.muted, mb: 0.5 }}>PREVIEW — what {submission.firstName || "the client"} gets</Typography>
            <Box
              data-testid="feedback-preview"
              sx={{ border: `1px dashed ${CW.line}`, borderRadius: "10px", p: 1.5, fontSize: 14, "& p": { m: "0 0 6px" }, wordBreak: "break-word" }}
              dangerouslySetInnerHTML={{ __html: finalHtml || "<p><i>Nothing yet</i></p>" }}
            />
          </Box>
        ) : null}

        {!submission.memberId && !submission.email ? (
          <Alert severity="warning" sx={{ mt: 2 }}>
            This person has no portal account or email — feedback can't be sent.
          </Alert>
        ) : null}

        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center", mt: 2 }}>
          <FormControlLabel
            sx={{ m: 0 }}
            control={<Checkbox size="small" checked={sendChat} disabled={!submission.memberId} onChange={(e) => setSendChat(e.target.checked)} />}
            label={<Typography sx={{ fontSize: 13.5 }}>Chat</Typography>}
          />
          <FormControlLabel
            sx={{ m: 0 }}
            control={<Checkbox size="small" checked={sendEmail} disabled={!submission.email} onChange={(e) => setSendEmail(e.target.checked)} />}
            label={<Typography sx={{ fontSize: 13.5 }}>Email</Typography>}
          />
          <Box sx={{ flex: 1 }} />
          <button type="button" className="small-contained-outlined-button" onClick={() => (preview ? setPreview(false) : openPreview())} style={{ opacity: canSend ? 1 : 0.55 }}>
            {preview ? "Hide preview" : "Preview"}
          </button>
          <button
            type="button"
            className="small-contained-button"
            onClick={send}
            disabled={!canSend || sending || Boolean(uploading) || (!sendChat && !sendEmail)}
          >
            {sending ? "Sending…" : "Send feedback"}
          </button>
        </Box>
      </Box>
    </Panel>
  );
}
